import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, apiImageUrl } from '../../utils/streamApi';
import IndustrialLayout from './IndustrialLayout';
import ReportDataMenu from '../../components/ReportDataMenu';
import SessionClipModal from '../../components/SessionClipModal';

// ── Cleaning Report ───────────────────────────────────────────────────────────
// When each floor zone was cleaned on a given day, from the floor_cleaning ledger.
// Per zone, in the order a facility manager reads it:
//   last cleaned   — the question they actually ask ("was reception done today?")
//   coverage       — how much of the floor the cleaner's feet came near
//   sessions       — each clean with its time and three proof photos + a heatmap
//
// Cleaning time is integrated, not end minus start: a pause to refill the bucket
// keeps one session but is not counted as cleaning.

const fmt = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};
const clock = (ts) => ts ? new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const hhmmTs = (dayStart, hhmm) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return Number.isFinite(h) ? dayStart + h * 3600 + (m || 0) * 60 : null;
};
const covColor = (c) => c >= 70 ? 'var(--ind-ok)' : c >= 40 ? 'var(--ind-accent)' : 'var(--ind-bad, #e74c3c)';

export default function IndustrialCleaningReportPage() {
  const [date, setDate]       = useState(todayStr());
  const [cameras, setCameras] = useState([]);
  const [scope, setScope]     = useState('');       // '' = every camera with cleaning zones
  const [rep, setRep]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [openZone, setOpenZone] = useState(null);   // zone whose sessions are expanded
  const [live, setLive]       = useState(null);     // real-time read, today only
  const [shot, setShot]       = useState(null);     // photo open in the lightbox
  const [clip, setClip]       = useState(null);     // session whose footage is open
  const nav = useNavigate();

  const isToday = date === todayStr();

  useEffect(() => {
    apiFetch('/ui/cameras').then(r => r.json()).then(d => setCameras(d.cameras || [])).catch(() => {});
  }, []);

  // Every configured zone — so a zone nobody cleaned still gets a row saying so.
  const configured = useMemo(() => cameras.flatMap(c =>
    (c.detectors || []).includes('floor_cleaning')
      ? (c.detector_config?.floor_cleaning?.zones || []).map(z => ({
          cam_idx: c.idx, cam_name: c.name || `Camera ${c.idx}`, zone: z.name,
          work_start: c.detector_config.floor_cleaning.work_start,
          work_end: c.detector_config.floor_cleaning.work_end }))
      : []), [cameras]);
  const cleanCams = useMemo(() => [...new Map(configured.map(z => [z.cam_idx, z.cam_name])).entries()]
    .map(([idx, name]) => ({ idx, name })), [configured]);

  const load = useCallback(() => {
    const q = new URLSearchParams({ date: date.replace(/-/g, '') });
    if (scope !== '') q.set('cam', scope);
    apiFetch(`/ui/cleaning/report?${q}`).then(r => r.json())
      .then(d => { setRep(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [date, scope]);

  useEffect(() => {
    setLoading(true); load();
    if (!isToday) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, isToday]);

  // Real-time read off the running detectors — the ledger only learns a session
  // started once it passes the minimum length, so this is what says "cleaning now".
  useEffect(() => {
    if (!isToday) { setLive(null); return; }
    let alive = true;
    const tick = () => apiFetch('/ui/cleaning/live').then(r => r.json())
      .then(d => { if (alive) setLive(d); }).catch(() => {});
    tick(); const t = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [isToday]);

  const liveZones = useMemo(() => (live?.cameras || [])
    .filter(c => scope === '' || String(c.cam_idx) === String(scope))
    .flatMap(c => (c.zones || []).map(z => ({ ...z, cam_idx: c.cam_idx, cam_name: c.cam_name }))), [live, scope]);
  const liveOf = (r) => liveZones.find(z => z.cam_idx === r.cam_idx && z.zone === r.zone);

  // Report rows merged with configured zones, so "not cleaned" is a row, not a gap.
  const rows = useMemo(() => {
    const got = rep?.zones || [];
    const key = (r) => `${r.cam_idx}:${r.zone}`;
    const have = new Set(got.map(key));
    const empty = configured
      .filter(z => (scope === '' || String(z.cam_idx) === String(scope)) && !have.has(key(z)))
      .map(z => ({ ...z, total_secs: 0, best_coverage: 0, last_cleaned: null, cleaning_now: false, sessions: [] }));
    return [...got, ...empty].sort((a, b) => a.cam_idx - b.cam_idx || String(a.zone).localeCompare(String(b.zone)));
  }, [rep, configured, scope]);

  const dayStart = rep?.day_start || 0;
  const totals = useMemo(() => ({
    sessions: rows.reduce((a, r) => a + r.sessions.length, 0),
    secs:     rows.reduce((a, r) => a + r.total_secs, 0),
    cleaned:  rows.filter(r => r.sessions.length).length,
    last:     rows.reduce((m, r) => Math.max(m, r.last_cleaned || 0), 0),
  }), [rows]);

  const shiftDay = (n) => {
    const d = new Date(date + 'T12:00:00');
    d.setDate(d.getDate() + n);
    if (d > new Date()) return;
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };

  const openShot = (name, label) =>
    apiImageUrl(`/ui/cleaning/photo/${encodeURIComponent(name)}`).then(url => setShot({ url, label })).catch(() => {});

  // Esc closes the lightbox, like the other report popups.
  useEffect(() => {
    if (!shot) return;
    const onKey = e => { if (e.key === 'Escape') setShot(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shot]);

  if (!loading && !rows.length && !configured.length) {
    return (
      <IndustrialLayout eyebrow="Cleaning" title="Cleaning Report" subtitle="NO FLOOR ZONES CONFIGURED">
        <div className="ind-card">
          <div className="ind-empty" style={{ padding: 28, lineHeight: 1.8 }}>
            <b>No floor is being measured yet.</b><br />
            Open <b>Cleaning Zones</b>, pick the camera, and draw the floor housekeeping is expected
            to clean — each cleaning session is recorded from then on.<br />
            <button className="ind-pill on" style={{ marginTop: 14 }}
              onClick={() => nav('/industrial/cleaning')}>🧹 Set up cleaning zones</button>
          </div>
        </div>
      </IndustrialLayout>
    );
  }

  return (
    <IndustrialLayout eyebrow="Cleaning" title="Cleaning Report"
      subtitle={`${rep?.date || date} · ${totals.cleaned} OF ${rows.length} ZONE${rows.length === 1 ? '' : 'S'} CLEANED`}>

      <div className="ind-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <span className="ind-eye">Day</span>
          <button className="ind-pill" onClick={() => shiftDay(-1)}>‹</button>
          <input type="date" value={date} max={todayStr()} onChange={e => setDate(e.target.value)} style={sel} />
          <button className="ind-pill" onClick={() => shiftDay(1)} disabled={isToday}>›</button>
          {!isToday && <button className="ind-pill" onClick={() => setDate(todayStr())}>Today</button>}
          {cleanCams.length > 1 && (
            <select value={scope} onChange={e => setScope(e.target.value)} style={{ ...sel, marginLeft: 12 }}>
              <option value="">All cameras</option>
              {cleanCams.map(c => <option key={c.idx} value={c.idx}>🎥 {c.name}</option>)}
            </select>
          )}
          <span style={{ flex: 1 }} />
          {(() => {
            // Data is reset per camera — the one picked, or the only cleaning camera there is.
            const c = scope !== '' ? cleanCams.find(x => String(x.idx) === String(scope))
                                   : (cleanCams.length === 1 ? cleanCams[0] : null);
            if (!c) return null;
            const its = configured.filter(z => z.cam_idx === c.idx).map(z => z.zone).filter(Boolean);
            return <ReportDataMenu kind="cleaning" cam={c.idx} camName={c.name} items={its} onChanged={load} />;
          })()}
        </div>

        {/* ── CLEANING NOW — today only, straight off the running detectors ── */}
        {isToday && liveZones.some(z => z.cleaning) && (
          <div style={{ border: '1px solid var(--ind-ok)', borderRadius: 10, padding: 12, marginBottom: 16,
            background: 'rgba(34,197,94,0.08)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="ind-eye">Cleaning now</span>
            {liveZones.filter(z => z.cleaning).map(z => (
              <span key={`${z.cam_idx}:${z.zone}`} style={{ fontSize: 12, color: 'var(--ind-tx)' }}>
                🧹 <b>{z.zone}</b> · {fmt(z.secs)} · {z.coverage}% covered
              </span>
            ))}
          </div>
        )}

        {loading && <div className="ind-empty" style={{ padding: 30 }}>Loading…</div>}

        {!loading && (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
              <Stat label="ZONES CLEANED" val={`${totals.cleaned}/${rows.length}`}
                color={totals.cleaned === rows.length ? 'var(--ind-ok)' : 'var(--ind-accent)'} />
              <Stat label="SESSIONS" val={totals.sessions} />
              <Stat label="TIME CLEANING" val={fmt(totals.secs)} small />
              <Stat label="LAST CLEANED" val={totals.last ? clock(totals.last) : '—'} small />
            </div>

            <div style={{ border: '1px solid var(--ind-line)', borderRadius: 8, overflow: 'hidden' }}>
              {rows.map((r, i) => {
                const key  = `${r.cam_idx}:${r.zone}`;
                const open = openZone === key;
                const lz   = isToday ? liveOf(r) : null;
                // Timeline over housekeeping hours when set, else 06:00–22:00.
                const from = hhmmTs(dayStart, r.work_start) ?? dayStart + 6 * 3600;
                let to     = hhmmTs(dayStart, r.work_end)   ?? dayStart + 22 * 3600;
                if (to <= from) to += 86400;
                const span = Math.max(1, to - from);
                const pct  = (ts) => Math.max(0, Math.min(100, ((ts - from) / span) * 100));
                return (
                  <div key={key} style={{ borderTop: i ? '1px solid var(--ind-line)' : 'none' }}>
                    <div onClick={() => setOpenZone(open ? null : key)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', flexWrap: 'wrap' }}>
                      <div style={{ width: 220, flexShrink: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--ind-tx)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {lz?.cleaning && <span title="Being cleaned right now"
                            style={{ width: 7, height: 7, borderRadius: 99, background: 'var(--ind-ok)', display: 'inline-block' }} />}
                          🧹 {r.zone}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--ind-tx3)' }}>
                          {r.cam_name}{r.work_start && r.work_end ? ` · hours ${r.work_start}–${r.work_end}` : ''}
                        </div>
                      </div>

                      <div style={{ flex: '1 1 200px', position: 'relative', height: 22, background: 'var(--ind-ink)',
                        border: '1px solid var(--ind-line)', borderRadius: 5, overflow: 'hidden' }}>
                        {r.sessions.map((s, j) => (
                          <div key={j} title={`${clock(s.start)}–${clock(s.end)} · ${fmt(s.secs)} · ${s.coverage}%`}
                            style={{ position: 'absolute', top: 0, bottom: 0, left: `${pct(s.start)}%`,
                              width: `${Math.max(0.8, pct(s.end) - pct(s.start))}%`,
                              background: s.open ? 'var(--ind-ok)' : 'rgba(34,197,94,0.62)' }} />
                        ))}
                        {!r.sessions.length && (
                          <span style={{ position: 'absolute', left: 8, top: 3, fontSize: 11, color: 'var(--ind-tx3)' }}>
                            {isToday ? 'Not cleaned yet today' : 'Not cleaned'}
                          </span>
                        )}
                      </div>

                      <div style={{ width: 92, textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--ind-mono)',
                          color: r.sessions.length ? covColor(r.best_coverage) : 'var(--ind-tx3)' }}>
                          {r.sessions.length ? `${r.best_coverage}%` : '—'}
                        </div>
                        <div style={{ fontSize: 9.5, color: 'var(--ind-tx3)' }}>best coverage</div>
                      </div>
                      <div style={{ width: 92, textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontFamily: 'var(--ind-mono)', color: 'var(--ind-tx2)' }}>
                          {r.last_cleaned ? clock(r.last_cleaned) : '—'}
                        </div>
                        <div style={{ fontSize: 9.5, color: 'var(--ind-tx3)' }}>last cleaned</div>
                      </div>
                      <span style={{ color: 'var(--ind-tx3)', fontSize: 11, width: 12 }}>{open ? '▾' : '▸'}</span>
                    </div>

                    {open && (
                      <div style={{ padding: '0 12px 12px', fontSize: 12, overflowX: 'auto' }}>
                        {!r.sessions.length && <div className="ind-empty" style={{ padding: 12 }}>No cleaning recorded on this day.</div>}
                        {r.sessions.length > 0 && (
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr style={{ textAlign: 'left', color: 'var(--ind-tx3)', fontSize: 10.5 }}>
                              <th style={th}>Started</th><th style={th}>Finished</th><th style={th}>Cleaning</th>
                              <th style={th}>Coverage</th><th style={th}>Proof</th>
                            </tr></thead>
                            <tbody>
                              {r.sessions.map((s, j) => (
                                <tr key={j} style={{ borderTop: '1px solid var(--ind-line)' }}>
                                  <td style={td}>{clock(s.start)}</td>
                                  <td style={td}>{s.open ? <span style={{ color: 'var(--ind-ok)' }}>in progress</span> : clock(s.end)}</td>
                                  <td style={td}>{fmt(s.secs)}</td>
                                  <td style={{ ...td, color: covColor(s.coverage), fontWeight: 700 }}>{s.coverage}%</td>
                                  <td style={{ ...td, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    <button className="ind-pill" style={{ padding: '2px 8px' }}
                                      onClick={() => setClip({ camIdx: r.cam_idx, camName: r.cam_name, title: r.zone,
                                        start: s.start, end: s.open ? Date.now() / 1000 : s.end })}>▶ video</button>
                                    {(s.photos || []).map(p => {
                                      const kind = (p.match(/_(start|mid|end)\.jpg$/) || [])[1] || 'photo';
                                      return <button key={p} className="ind-pill" style={{ padding: '2px 8px' }}
                                        onClick={() => openShot(p, `${r.zone} · ${kind} · ${clock(s.start)}`)}>📷 {kind}</button>;
                                    })}
                                    {s.heatmap && <button className="ind-pill" style={{ padding: '2px 8px' }}
                                      onClick={() => openShot(s.heatmap, `${r.zone} · coverage ${s.coverage}%`)}>🟩 heatmap</button>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <p style={{ fontSize: 11, color: 'var(--ind-tx3)', marginTop: 14, lineHeight: 1.7, maxWidth: 820 }}>
              A session starts once someone has been cleaning for about half a minute and is kept only if it
              lasts the minimum session length. Coverage is the share of the zone the cleaner’s feet came near;
              in the heatmap, green cells were covered and red were missed. Floor hidden behind furniture or
              turnstiles always reads as missed — keep it out of the zone.
              {' '}<button className="ind-pill" style={{ padding: '2px 8px' }}
                onClick={() => nav('/industrial/cleaning')}>Edit cleaning zones</button>
            </p>
          </>
        )}
      </div>

      {clip && <SessionClipModal {...clip} onClose={() => setClip(null)} />}

      {shot && (
        <div onClick={() => setShot(null)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.78)',
          display: 'grid', placeItems: 'center', padding: 16, cursor: 'zoom-out' }}>
          <div style={{ maxWidth: '100%', textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ color: '#fff', fontSize: 13 }}>{shot.label}</span>
              <button className="ind-pill" style={{ marginLeft: 'auto', cursor: 'pointer' }}
                onClick={() => setShot(null)}>✕ Close</button>
            </div>
            <img src={shot.url} alt={shot.label} style={{ display: 'block', maxWidth: '100%', maxHeight: '80vh', borderRadius: 6 }} />
          </div>
        </div>
      )}
    </IndustrialLayout>
  );
}

function Stat({ label, val, color, small }) {
  return (
    <div style={{ flex: '1 1 110px', padding: '14px 12px', borderRadius: 10, textAlign: 'center',
      border: '1px solid var(--ind-line)', background: 'var(--ind-ink)' }}>
      <div style={{ fontSize: small ? 20 : 30, fontWeight: 700, fontFamily: 'var(--ind-mono)',
        lineHeight: 1.2, color: color || 'var(--ind-tx)' }}>{val}</div>
      <div style={{ fontSize: 10, letterSpacing: '.1em', color: 'var(--ind-tx3)', marginTop: 6 }}>{label}</div>
    </div>
  );
}

const sel = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12 };
const th  = { padding: '5px 10px', fontWeight: 600 };
const td  = { padding: '5px 10px', fontFamily: 'var(--ind-mono)' };
